import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, ImageOverlay, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Satellite, RefreshCw, Loader2, CloudOff } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import MapTouchGate from '../map/MapTouchGate';
import SatelliteVigourChart from './SatelliteVigourChart';
import satelliteService from '../../services/satellite';
import { errorMessage } from '../../utils/apiResponse';
import { TILE_LAYERS, MAP_MAX_ZOOM, geoJSONToLeaflet } from '../../utils/leafletGeo';
import { formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import {
  LAYER_HELP,
  LAYER_LABEL,
  RAMP_CSS,
  fmtIndex,
  insideMask,
  paint,
  pixelsOf,
  toDataUrl,
  type PaintRange,
} from '../../utils/satelliteRender';
import type { GeoJSONPolygon } from '../../types/orchard';
import type {
  SatelliteFarm,
  SatelliteLayer,
  SatelliteScale,
  SatelliteSceneStats,
  SatelliteSeries,
  SatelliteSeriesPoint,
  SatelliteTarget,
} from '../../types/satellite';

interface Decoded {
  index: ImageData;
  nir: ImageData;
  bbox: [number, number, number, number];
  width: number;
  height: number;
  pixel_m: number | null;
}

const PLACE_WORD = { farm: 'farm', block: 'block', cultivar_area: 'cultivar area' } as const;

function FitTo({ boundary }: { boundary: GeoJSONPolygon }) {
  const map = useMap();
  const key = JSON.stringify(boundary?.coordinates?.[0]?.[0] ?? null);
  useEffect(() => {
    const ring = boundary?.coordinates?.[0]?.length ? geoJSONToLeaflet(boundary.coordinates) : [];
    if (ring.length) map.fitBounds(L.latLngBounds(ring), { padding: [16, 16], maxZoom: 18, animate: false });
    // Fit once per place; zooming afterwards is the reader's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

/**
 * Sentinel-2 over a farm, a block or a cultivar area: NDVI, NDRE or false-colour
 * near-infrared laid over the map for a chosen date, the figures for that date,
 * and (below the farm) how the place has moved through the season.
 *
 * Switching on is done from the farm, because it is the farm's rectangle that
 * is sent to the Copernicus Data Space. Block and cultivar area pages read the
 * farm's images and say where to switch it on.
 */
export default function SatellitePanel({
  farmId,
  farmName,
  target,
  outlines = [],
  readOnly = false,
}: {
  farmId: number;
  farmName?: string;
  target: SatelliteTarget;
  /** Other boundaries to draw for orientation (the farm's blocks, a block's areas). */
  outlines?: GeoJSONPolygon[];
  readOnly?: boolean;
}) {
  const timezone = useOrgTimezone();
  const [farm, setFarm] = useState<SatelliteFarm | null>(null);
  const [series, setSeries] = useState<SatelliteSeries | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'enable' | 'disable' | 'refresh' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [sceneId, setSceneId] = useState<number | null>(null);
  const [showCloudy, setShowCloudy] = useState(false);
  const [layer, setLayer] = useState<SatelliteLayer>('ndvi');
  const [scale, setScale] = useState<SatelliteScale>('fixed');
  const [opacity, setOpacity] = useState(0.85);
  const [decoded, setDecoded] = useState<Decoded | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<{ url: string; range: PaintRange; version: number } | null>(null);
  const [sceneStats, setSceneStats] = useState<SatelliteSceneStats | null>(null);
  const cache = useRef(new Map<number, Decoded>());

  const loadFarm = useCallback(async () => {
    try {
      const res = await satelliteService.getFarm(farmId);
      if (res.success && res.data) setFarm(res.data);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the satellite imagery'));
    }
  }, [farmId]);
  useEffect(() => { loadFarm(); }, [loadFarm]);

  const okScenes = useMemo(() => (farm?.scenes ?? []).filter((s) => s.status === 'ok'), [farm]);
  const sceneKey = okScenes.map((s) => s.id).join(',');

  useEffect(() => {
    if (!farm?.enabled && !okScenes.length) return;
    if (!okScenes.length) { setSeries(null); return; }
    let live = true;
    satelliteService.getSeries(target.type, target.id)
      .then((res) => { if (live && res.success && res.data) setSeries(res.data); })
      .catch((e) => { if (live) setError(errorMessage(e, 'Could not work out the figures')); });
    return () => { live = false; };
    // A new pass or a changed place reloads the figures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneKey, target.type, target.id]);

  // The dates offered: those clear over this place, newest first.
  const dates: SatelliteSeriesPoint[] = useMemo(() => {
    const points = series?.points ?? [];
    return points.filter((p) => showCloudy || p.usable).slice().reverse();
  }, [series, showCloudy]);

  useEffect(() => {
    if (!dates.length) { setSceneId(null); return; }
    if (sceneId == null || !dates.some((d) => d.scene_id === sceneId)) setSceneId(dates[0].scene_id);
  }, [dates, sceneId]);

  // The selected date's images, decoded once each.
  useEffect(() => {
    if (sceneId == null) { setDecoded(null); return; }
    const hit = cache.current.get(sceneId);
    if (hit) { setDecoded(hit); return; }
    let live = true;
    setImageError(null);
    (async () => {
      try {
        const res = await satelliteService.getImages(sceneId);
        if (!res.success || !res.data || !res.data.bbox || !res.data.width || !res.data.height) throw new Error('No image');
        const [index, nir] = await Promise.all([pixelsOf(res.data.index), pixelsOf(res.data.nir)]);
        const d: Decoded = {
          index, nir, bbox: res.data.bbox, width: res.data.width, height: res.data.height, pixel_m: res.data.pixel_m,
        };
        cache.current.set(sceneId, d);
        if (live) setDecoded(d);
      } catch (e) {
        if (live) { setDecoded(null); setImageError(errorMessage(e, 'Could not load that image')); }
      }
    })();
    return () => { live = false; };
  }, [sceneId]);

  const mask = useMemo(
    () => (decoded && target.boundary ? insideMask(target.boundary, decoded.bbox, decoded.width, decoded.height) : null),
    [decoded, target.boundary],
  );

  useEffect(() => {
    if (!decoded || !mask) { setOverlay(null); return; }
    const out = new Uint8ClampedArray(decoded.width * decoded.height * 4);
    const range = paint(out, decoded.index.data, decoded.nir.data, { layer, scale, mask });
    setOverlay((prev) => ({ url: toDataUrl(decoded.width, decoded.height, out), range, version: (prev?.version ?? 0) + 1 }));
  }, [decoded, mask, layer, scale]);

  // On the farm, every block's figures for the date.
  useEffect(() => {
    if (target.type !== 'farm' || sceneId == null) { setSceneStats(null); return; }
    let live = true;
    satelliteService.getSceneStats(sceneId)
      .then((res) => { if (live && res.success && res.data) setSceneStats(res.data); })
      .catch(() => { if (live) setSceneStats(null); });
    return () => { live = false; };
  }, [target.type, sceneId]);

  const setEnabled = async (enabled: boolean) => {
    setBusy(enabled ? 'enable' : 'disable');
    setMessage(null);
    try {
      const res = await satelliteService.setEnabled(farmId, enabled);
      if (res.success && res.data) setFarm(res.data);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not change satellite imagery for this farm'));
    } finally {
      setBusy(null);
    }
  };

  const refresh = async () => {
    setBusy('refresh');
    setMessage(null);
    try {
      const res = await satelliteService.refresh(farmId);
      if (res.success && res.data) {
        setFarm(res.data.farm);
        const { added, failed, pending } = res.data;
        const parts = [added ? `${added} new image${added === 1 ? '' : 's'}` : 'No new images'];
        if (failed) parts.push(`${failed} could not be fetched and will be tried again`);
        if (pending) parts.push(`${pending} more waiting, fetched within the hour`);
        setMessage(`${parts.join('. ')}.`);
      }
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not check for new images'));
    } finally {
      setBusy(null);
    }
  };

  const point = dates.find((d) => d.scene_id === sceneId) ?? null;
  const placeWord = PLACE_WORD[target.type];
  const ringPositions = useMemo(
    () => (target.boundary?.coordinates?.[0]?.length ? geoJSONToLeaflet(target.boundary.coordinates) : []),
    [target.boundary],
  );
  const outlinePositions = useMemo(
    () => outlines.filter((o) => o?.coordinates?.[0]?.length).map((o) => geoJSONToLeaflet(o.coordinates)),
    [outlines],
  );

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex items-start gap-2 min-w-0">
        <Satellite className="h-4 w-4 text-neutral-500 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-neutral-700">Satellite: NDVI and near-infrared</h3>
          <p className="text-xs text-neutral-500">
            Sentinel-2, 10 m squares, a new pass about every five days when it is clear.
          </p>
        </div>
      </div>
      {farm?.enabled && target.type === 'farm' && !readOnly && farm.configured && (
        <Button size="sm" variant="outline" onClick={refresh} disabled={busy != null}>
          {busy === 'refresh' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Check for new images
        </Button>
      )}
    </div>
  );

  if (!farm) {
    return (
      <Card>
        <CardContent className="p-5 space-y-3">
          {header}
          {error ? <p className="text-sm text-error-700">{error}</p> : <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />}
        </CardContent>
      </Card>
    );
  }

  // Not switched on, and nothing kept from before.
  if (!farm.enabled && !okScenes.length) {
    return (
      <Card>
        <CardContent className="p-5 space-y-3">
          {header}
          {!farm.configured ? (
            <p className="text-sm text-neutral-600">Satellite imagery is not set up on this server yet.</p>
          ) : target.type === 'farm' ? (
            <>
              <p className="text-sm text-neutral-600">
                See which parts of the farm are growing strongly and which are falling behind, date by date.
                Switching it on fetches the past year of clear passes, then each new one.
              </p>
              <p className="text-xs text-neutral-500">
                To fetch the images, the rectangle around this farm (not its boundary, and nothing else) is sent
                to the European Space Agency&rsquo;s Copernicus Data Space. See the{' '}
                <Link to="/privacy" className="underline">Privacy Policy</Link>.
              </p>
              {error && <p className="text-sm text-error-700">{error}</p>}
              {!readOnly && (
                <Button size="sm" onClick={() => setEnabled(true)} disabled={busy != null}>
                  {busy === 'enable' && <Loader2 className="h-4 w-4 animate-spin" />}
                  {busy === 'enable' ? 'Fetching the newest images…' : 'Switch on satellite imagery'}
                </Button>
              )}
            </>
          ) : (
            <p className="text-sm text-neutral-600">
              Satellite imagery is off for {farmName ?? 'this farm'}. Switch it on from the{' '}
              <Link to={`/orchard/farms/${farmId}`} className="underline">farm&rsquo;s page</Link>.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  const legend = overlay?.range ? (
    <div className="space-y-1">
      <div className="h-2.5 rounded" style={{ background: RAMP_CSS }} />
      <div className="flex justify-between text-[11px] text-neutral-500">
        <span>{fmtIndex(overlay.range[0])} lower</span>
        <span>higher {fmtIndex(overlay.range[1])}</span>
      </div>
    </div>
  ) : null;

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        {header}

        {error && <p className="text-sm text-error-700">{error}</p>}
        {message && <p className="text-sm text-green-700">{message}</p>}
        {farm.last_error && (
          <p className="text-sm text-amber-700">{farm.last_error}</p>
        )}
        {!farm.enabled && (
          <p className="text-xs text-neutral-500">
            Switched off: these are the images fetched before, and no new ones are being fetched.
          </p>
        )}

        {!okScenes.length ? (
          <p className="text-sm text-neutral-600">
            {farm.pending > 0
              ? `${farm.pending} passes found. They are being fetched and will appear within the hour.`
              : 'No clear passes have been fetched yet. They are looked for every day.'}
          </p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-xs text-neutral-600">
                Date
                <select
                  className="mt-1 block w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm"
                  value={sceneId ?? ''}
                  onChange={(e) => setSceneId(Number(e.target.value))}
                  disabled={!dates.length}
                >
                  {dates.map((d) => (
                    <option key={d.scene_id} value={d.scene_id}>
                      {formatLocalDateInTz(d.acquired_on, timezone)}
                      {d.usable ? '' : ` (${Math.round(d.clear_pct ?? 0)}% clear)`}
                    </option>
                  ))}
                </select>
              </label>
              <div className="text-xs text-neutral-600">
                Show
                <div className="mt-1 flex flex-wrap gap-1" role="group" aria-label="Layer">
                  {(['ndvi', 'ndre', 'nir'] as SatelliteLayer[]).map((l) => (
                    <button
                      key={l}
                      type="button"
                      aria-pressed={layer === l}
                      onClick={() => setLayer(l)}
                      className={`rounded-md border px-2.5 py-1 text-sm ${layer === l
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50'}`}
                    >
                      {LAYER_LABEL[l]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-neutral-600">
              <label className="inline-flex items-center gap-1.5">
                <input type="checkbox" checked={showCloudy} onChange={(e) => setShowCloudy(e.target.checked)} />
                Include cloudy dates
              </label>
              {layer !== 'nir' && (
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={scale === 'stretch'}
                    onChange={(e) => setScale(e.target.checked ? 'stretch' : 'fixed')}
                  />
                  Stretch colours to this {placeWord}
                </label>
              )}
              <label className="inline-flex items-center gap-1.5">
                Opacity
                <input
                  type="range"
                  min={0.2}
                  max={1}
                  step={0.05}
                  value={opacity}
                  onChange={(e) => setOpacity(Number(e.target.value))}
                  className="w-24"
                />
              </label>
            </div>

            {!dates.length ? (
              <p className="flex items-center gap-2 text-sm text-neutral-600">
                <CloudOff className="h-4 w-4 flex-shrink-0" />
                No date was clear over this {placeWord}. Tick &ldquo;Include cloudy dates&rdquo; to see them anyway.
              </p>
            ) : (
              <>
                <div className="h-72 sm:h-96 rounded-lg overflow-hidden border border-neutral-200">
                  <MapContainer
                    center={ringPositions[0] ?? [-37, 145]}
                    zoom={16}
                    maxZoom={MAP_MAX_ZOOM}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      url={TILE_LAYERS.satellite.url}
                      attribution={TILE_LAYERS.satellite.attribution}
                      maxNativeZoom={TILE_LAYERS.satellite.maxNativeZoom}
                      maxZoom={TILE_LAYERS.satellite.maxZoom}
                    />
                    {overlay && decoded && (
                      <ImageOverlay
                        key={overlay.version}
                        url={overlay.url}
                        bounds={[[decoded.bbox[1], decoded.bbox[0]], [decoded.bbox[3], decoded.bbox[2]]]}
                        opacity={opacity}
                        className="[image-rendering:pixelated]"
                        attribution="Contains modified Copernicus Sentinel data"
                      />
                    )}
                    {outlinePositions.map((ring, i) => (
                      <Polygon key={i} positions={ring} pathOptions={{ color: '#ffffff', weight: 1, opacity: 0.7, fill: false }} />
                    ))}
                    <Polygon positions={ringPositions} pathOptions={{ color: '#ffffff', weight: 2.5, fill: false }} />
                    <FitTo boundary={target.boundary} />
                    <MapTouchGate />
                  </MapContainer>
                </div>
                {imageError && <p className="text-sm text-error-700">{imageError}</p>}
                {legend}
                <p className="text-xs text-neutral-500">{LAYER_HELP[layer]}</p>

                {point && (
                  <div className="rounded-md bg-neutral-50 p-3 text-sm text-neutral-700 space-y-1">
                    <p>
                      <span className="font-medium">{formatLocalDateInTz(point.acquired_on, timezone)}</span>
                      {' · '}NDVI {fmtIndex(point.ndvi_mean)}
                      <span className="text-neutral-500"> (most of it {fmtIndex(point.ndvi_p10)} to {fmtIndex(point.ndvi_p90)})</span>
                      {' · '}NDRE {fmtIndex(point.ndre_mean)}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {Math.round(point.clear_pct ?? 0)}% of the {placeWord} was clear
                      {point.clear_pct != null && point.clear_pct < 100 ? ', and the figures describe that part' : ''}.
                      {' '}{point.clear} squares of {decoded?.pixel_m ?? 10} m.
                      {point.few_pixels && ` That is few: read it as a rough guide for a ${placeWord} this small.`}
                    </p>
                  </div>
                )}
              </>
            )}

            {target.type === 'farm' && sceneStats && sceneStats.blocks.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Blocks on this date</h4>
                <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200">
                  {sceneStats.blocks.map((b) => {
                    const diff = b.ndvi_mean != null && sceneStats.farm.ndvi_mean != null
                      ? b.ndvi_mean - sceneStats.farm.ndvi_mean : null;
                    return (
                      <li key={b.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2 text-sm">
                        <Link to={`/orchard/blocks/${b.id}`} className="min-w-0 break-words font-medium text-primary-700 hover:underline">
                          {b.name}
                        </Link>
                        <span className="text-neutral-700">
                          {b.usable ? (
                            <>
                              NDVI {fmtIndex(b.ndvi_mean)}
                              {diff != null && (
                                <span className={diff < -0.03 ? 'text-red-700' : diff > 0.03 ? 'text-green-700' : 'text-neutral-500'}>
                                  {' '}({diff >= 0 ? '+' : '−'}{Math.abs(diff).toFixed(2)} on the farm)
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-neutral-500">
                              {b.pixels ? `Cloud: ${Math.round(b.clear_pct ?? 0)}% clear` : 'Too small to read'}
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {series && (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Through the season</h4>
                <SatelliteVigourChart
                  points={series.points}
                  selectedSceneId={sceneId}
                  onSelect={(id) => { setShowCloudy(false); setSceneId(id); }}
                  timezone={timezone}
                />
              </div>
            )}
          </>
        )}

        {farm.enabled && (
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-500">
            <span>
              {farm.last_searched_at
                ? `Last looked for new passes ${formatDateTimeInTz(farm.last_searched_at, timezone)}.`
                : 'Looking for passes.'}
              {farm.pending > 0 && okScenes.length > 0 && ` ${farm.pending} older passes still being fetched.`}
            </span>
            {target.type === 'farm' && !readOnly && (
              <button
                type="button"
                className="underline hover:text-neutral-700"
                onClick={() => setEnabled(false)}
                disabled={busy != null}
              >
                Switch off
              </button>
            )}
          </div>
        )}
        {okScenes.length > 0 && (
          <p className="text-[11px] text-neutral-400">Contains modified Copernicus Sentinel data.</p>
        )}
      </CardContent>
    </Card>
  );
}
